import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { axiosInstance } from "../utils/axiosConfig";
import toast from "react-hot-toast";
import "../styles/NotificationSettings.css";

interface Settings {
  emailEnabled: boolean;
  dailyJobAlerts: boolean;
  weeklyDigest: boolean;
  applicationUpdates: boolean;
  interviewReminders: boolean;
  jobExpirationWarnings: boolean;
  newRecommendations: boolean;
  inAppEnabled: boolean;
  soundEnabled: boolean;
  dailyAlertTime: string;
  weeklyDigestDay: string;
  reminderHoursBefore: number;
  // Phase 15.5 — days before a job posting's end date to alert (null = default 3)
  postEndAlertDays?: number | null;
}

const NotificationSettings: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState<Settings>({
    emailEnabled: true,
    dailyJobAlerts: true,
    weeklyDigest: true,
    applicationUpdates: true,
    interviewReminders: true,
    jobExpirationWarnings: false,
    newRecommendations: true,
    inAppEnabled: true,
    soundEnabled: false,
    dailyAlertTime: "09:00",
    weeklyDigestDay: "monday",
    reminderHoursBefore: 24,
    postEndAlertDays: null,
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get("/auth/notifications/settings");
      setSettings(response.data.settings);
    } catch (error) {
      toast.error("Failed to load notification settings");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await axiosInstance.put("/auth/notifications/settings", settings);
      toast.success("Settings saved successfully!");
    } catch (error) {
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = (key: keyof Settings) => {
    setSettings((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleChange = (key: keyof Settings, value: any) => {
    setSettings((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const sendTestNotification = async () => {
    try {
      await axiosInstance.post("/auth/notifications/send-test");
      toast.success("Test notification sent!");
    } catch (error) {
      toast.error("Failed to send test notification");
    }
  };

  if (loading) {
    return (
      <div className="ns-container">
        <div className="ns-loading">
          <div className="ns-spinner" />
          <p>Loading settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="ns-container">
      <div className="ns-header">
        <button onClick={() => navigate("/dashboard")} className="ns-back-btn">
          ← Back to Dashboard
        </button>
        <h1>🔔 Notification Settings</h1>
        <p>Manage how and when you receive notifications</p>
      </div>

      <div className="ns-sections">
        {/* Email Notifications */}
        <div className="ns-section">
          <div className="ns-section-header">
            <h2>📧 Email Notifications</h2>
            <label className="ns-master-toggle">
              <input
                type="checkbox"
                checked={settings.emailEnabled}
                onChange={() => handleToggle("emailEnabled")}
              />
              <span className="ns-toggle-slider" />
            </label>
          </div>
          
          <p className="ns-section-description">
            Receive important updates and alerts via email
          </p>

          <div className={`ns-options ${!settings.emailEnabled ? "disabled" : ""}`}>
            <div className="ns-option">
              <div className="ns-option-info">
                <label>Daily Job Alerts</label>
                <span>Get personalized job recommendations every day</span>
              </div>
              <label className="ns-toggle">
                <input
                  type="checkbox"
                  checked={settings.dailyJobAlerts}
                  onChange={() => handleToggle("dailyJobAlerts")}
                  disabled={!settings.emailEnabled}
                />
                <span className="ns-toggle-slider" />
              </label>
            </div>

            {settings.dailyJobAlerts && settings.emailEnabled && (
              <div className="ns-option-sub">
                <label>Preferred Time</label>
                <select
                  value={settings.dailyAlertTime}
                  onChange={(e) => handleChange("dailyAlertTime", e.target.value)}
                >
                  <option value="06:00">6:00 AM</option>
                  <option value="07:00">7:00 AM</option>
                  <option value="08:00">8:00 AM</option>
                  <option value="09:00">9:00 AM</option>
                  <option value="10:00">10:00 AM</option>
                  <option value="12:00">12:00 PM</option>
                  <option value="18:00">6:00 PM</option>
                </select>
              </div>
            )}

            <div className="ns-option">
              <div className="ns-option-info">
                <label>Weekly Digest</label>
                <span>Summary of your job search activity each week</span>
              </div>
              <label className="ns-toggle">
                <input
                  type="checkbox"
                  checked={settings.weeklyDigest}
                  onChange={() => handleToggle("weeklyDigest")}
                  disabled={!settings.emailEnabled}
                />
                <span className="ns-toggle-slider" />
              </label>
            </div>

            {settings.weeklyDigest && settings.emailEnabled && (
              <div className="ns-option-sub">
                <label>Day of Week</label>
                <select
                  value={settings.weeklyDigestDay}
                  onChange={(e) => handleChange("weeklyDigestDay", e.target.value)}
                >
                  <option value="monday">Monday</option>
                  <option value="tuesday">Tuesday</option>
                  <option value="wednesday">Wednesday</option>
                  <option value="thursday">Thursday</option>
                  <option value="friday">Friday</option>
                  <option value="saturday">Saturday</option>
                  <option value="sunday">Sunday</option>
                </select>
              </div>
            )}

            <div className="ns-option">
              <div className="ns-option-info">
                <label>Application Updates</label>
                <span>Get notified when your application status changes</span>
              </div>
              <label className="ns-toggle">
                <input
                  type="checkbox"
                  checked={settings.applicationUpdates}
                  onChange={() => handleToggle("applicationUpdates")}
                  disabled={!settings.emailEnabled}
                />
                <span className="ns-toggle-slider" />
              </label>
            </div>

            <div className="ns-option">
              <div className="ns-option-info">
                <label>Interview Reminders</label>
                <span>Receive reminders before scheduled interviews</span>
              </div>
              <label className="ns-toggle">
                <input
                  type="checkbox"
                  checked={settings.interviewReminders}
                  onChange={() => handleToggle("interviewReminders")}
                  disabled={!settings.emailEnabled}
                />
                <span className="ns-toggle-slider" />
              </label>
            </div>

            {settings.interviewReminders && settings.emailEnabled && (
              <div className="ns-option-sub">
                <label>Remind Me</label>
                <select
                  value={settings.reminderHoursBefore}
                  onChange={(e) => handleChange("reminderHoursBefore", parseInt(e.target.value))}
                >
                  <option value="1">1 hour before</option>
                  <option value="3">3 hours before</option>
                  <option value="12">12 hours before</option>
                  <option value="24">24 hours before</option>
                  <option value="48">48 hours before</option>
                </select>
              </div>
            )}

            <div className="ns-option">
              <div className="ns-option-info">
                <label>New Recommendations</label>
                <span>Get notified about new job recommendations</span>
              </div>
              <label className="ns-toggle">
                <input
                  type="checkbox"
                  checked={settings.newRecommendations}
                  onChange={() => handleToggle("newRecommendations")}
                  disabled={!settings.emailEnabled}
                />
                <span className="ns-toggle-slider" />
              </label>
            </div>

            <div className="ns-option">
              <div className="ns-option-info">
                <label>Job Expiration Warnings</label>
                <span>Get notified when saved jobs are about to expire</span>
              </div>
              <label className="ns-toggle">
                <input
                  type="checkbox"
                  checked={settings.jobExpirationWarnings}
                  onChange={() => handleToggle("jobExpirationWarnings")}
                  disabled={!settings.emailEnabled}
                />
                <span className="ns-toggle-slider" />
              </label>
            </div>

            {/* Phase 15.5 — configurable lead time (design 11.6) */}
            <div className="ns-option">
              <div className="ns-option-info">
                <label>Expiry Alert Lead Time</label>
                <span>How many days before a posting closes to alert you (default 3)</span>
              </div>
              <input
                type="number"
                min={1}
                max={30}
                value={settings.postEndAlertDays ?? 3}
                onChange={(e) => {
                  const n = parseInt(e.target.value, 10);
                  setSettings((prev) => ({
                    ...prev,
                    postEndAlertDays: Number.isNaN(n) ? null : Math.min(30, Math.max(1, n)),
                  }));
                }}
                style={{ width: 70, padding: "6px 8px", borderRadius: 6, border: "1px solid #d1d5db" }}
                aria-label="Days before job posting end date to alert"
              />
            </div>
          </div>
        </div>

        {/* In-App Notifications */}
        <div className="ns-section">
          <div className="ns-section-header">
            <h2>📱 In-App Notifications</h2>
            <label className="ns-master-toggle">
              <input
                type="checkbox"
                checked={settings.inAppEnabled}
                onChange={() => handleToggle("inAppEnabled")}
              />
              <span className="ns-toggle-slider" />
            </label>
          </div>
          
          <p className="ns-section-description">
            Receive real-time notifications within the app
          </p>

          <div className={`ns-options ${!settings.inAppEnabled ? "disabled" : ""}`}>
            <div className="ns-option">
              <div className="ns-option-info">
                <label>Sound Notifications</label>
                <span>Play a sound when you receive new notifications</span>
              </div>
              <label className="ns-toggle">
                <input
                  type="checkbox"
                  checked={settings.soundEnabled}
                  onChange={() => handleToggle("soundEnabled")}
                  disabled={!settings.inAppEnabled}
                />
                <span className="ns-toggle-slider" />
              </label>
            </div>
          </div>
        </div>

        {/* Test & Actions */}
        <div className="ns-section">
          <div className="ns-section-header">
            <h2>🧪 Test Notifications</h2>
          </div>
          
          <p className="ns-section-description">
            Send a test notification to verify your settings
          </p>

          <button onClick={sendTestNotification} className="ns-test-btn">
            Send Test Notification
          </button>
        </div>
      </div>

      {/* Save Button */}
      <div className="ns-actions">
        <button
          onClick={handleSave}
          disabled={saving}
          className="ns-save-btn"
        >
          {saving ? "Saving..." : "💾 Save Settings"}
        </button>
      </div>
    </div>
  );
};

export default NotificationSettings;
